export const BALANCED_LINE_OPTIONS = [
  { value: "balanced:-3", label: "MBL −0.75" },
  { value: "balanced:-2", label: "MBL −0.50" },
  { value: "balanced:-1", label: "MBL −0.25" },
  { value: "balanced:0", label: "Most Balanced" },
  { value: "balanced:1", label: "MBL +0.25" },
  { value: "balanced:2", label: "MBL +0.50" },
  { value: "balanced:3", label: "MBL +0.75" },
];

export function formatFairOdd(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number.toFixed(2) : "–";
}

export function getLeagueFairMarketValues(
  fixture,
  market,
  { goalLine = "2.50", handicapLine = "0.00", raw = false } = {}
) {
  const output = raw
    ? (v) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null)
    : formatFairOdd;
  const fairMarkets = fixture?.fair_markets;
  if (market === "double-chance") {
    const p=fixture?.probabilities || fixture?.probs || fixture?.one_x_two;
    const values=[p?.homewinprob ?? p?.homewin,p?.drawprob ?? p?.draw,p?.awaywinprob ?? p?.awaywin];
    return [[0,1],[1,2],[0,2]].map(([a,b])=>values[a]!=null&&values[b]!=null&&Number(values[a])+Number(values[b])>0?output(1/(Number(values[a])+Number(values[b]))):output(null));
  }


  if (market === "over-under") {
    const line = resolveFixtureLine(fairMarkets?.asian_goal_lines, goalLine);
    const values = fairMarkets?.asian_goal_lines?.lines?.[line];
    return [output(values?.over), output(values?.under)];
  }

  if (market === "asian-handicap") {
    const line = resolveFixtureLine(fairMarkets?.asian_handicaps, handicapLine);
    const values = fairMarkets?.asian_handicaps?.lines?.[line];
    return [output(values?.home), output(values?.away)];
  }

  if (market === "btts") {
    return [output(fairMarkets?.btts?.yes), output(fairMarkets?.btts?.no)];
  }

  return [];
}

export function resolveFixtureLine(market, selection) {
  if (!String(selection).startsWith("balanced:")) {
    return selection;
  }

  const balanced = Number(market?.most_balanced_line);
  const offset = Number(String(selection).split(":")[1]);
  if (!Number.isFinite(balanced) || !Number.isInteger(offset)) {
    return null;
  }

  const line = (balanced + offset * 0.25).toFixed(2);
  return market?.lines?.[line] ? line : null;
}

export function isFixtureSpecificLine(selection) {
  return String(selection).startsWith("balanced:");
}

export function getFixtureLineSelection(fixtureId, globalSelection, overrides) {
  return overrides?.[String(fixtureId)] || globalSelection;
}

export function normalizeGoalLine(value) {
  const number = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(number) ? number.toFixed(2) : null;
}

export function resolveGoalLineSelection(fixture, market, selection) {
  if (selection === "betty-balanced") {
    return normalizeGoalLine(
      fixture?.fair_markets?.asian_goal_lines?.most_balanced_line
    );
  }
  if (selection === "market-balanced") {
    return normalizeGoalLine(market?.mostBalancedLine);
  }
  return normalizeGoalLine(selection);
}

export function getFixtureGoalLines(fixture, market) {
  return [
    ...new Set(
      [
        ...Object.keys(fixture?.fair_markets?.asian_goal_lines?.lines || {}),
        ...(market?.availableLines || []),
      ]
        .map(normalizeGoalLine)
        .filter(Boolean)
    ),
  ].sort((a, b) => Number(a) - Number(b));
}

export function getFixtureHandicapLines(fixture, market) {
  return [
    ...new Set(
      [
        ...Object.keys(fixture?.fair_markets?.asian_handicaps?.lines || {}),
        ...(market?.availableLines || []),
      ]
        .map(normalizeGoalLine)
        .filter(Boolean)
    ),
  ].sort((a, b) => Number(a) - Number(b));
}

export function resolveHandicapLineSelection(fixture, selection) {
  return String(selection).startsWith("balanced:")
    ? resolveFixtureLine(fixture?.fair_markets?.asian_handicaps, selection)
    : normalizeGoalLine(selection);
}

export function getDefaultHandicapSelection(fixtures) {
  const hasMostBalancedLine = (Array.isArray(fixtures) ? fixtures : []).some(
    (fixture) =>
      resolveFixtureLine(
        fixture?.fair_markets?.asian_handicaps,
        "balanced:0"
      ) !== null
  );
  return hasMostBalancedLine ? "balanced:0" : "0.00";
}

export function formatAsianHandicapLine(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "–";
  if (number === 0) return "0 (Draw No Bet)";
  return `${number > 0 ? "+" : "−"}${Math.abs(number)
    .toFixed(2)
    .replace(".", ",")}`;
}

export function hasStoredFairLine(fixture, market, line) {
  const marketKey =
    market === "over-under"
      ? "asian_goal_lines"
      : market === "asian-handicap"
      ? "asian_handicaps"
      : null;
  const normalizedLine = normalizeGoalLine(line);
  return Boolean(
    marketKey &&
      normalizedLine &&
      fixture?.fair_markets?.[marketKey]?.lines?.[normalizedLine]
  );
}

export function mergeTemporaryFairLine(fixture, market, line, values) {
  const marketKey =
    market === "over-under"
      ? "asian_goal_lines"
      : market === "asian-handicap"
      ? "asian_handicaps"
      : null;
  const normalizedLine = normalizeGoalLine(line);
  if (!marketKey || !normalizedLine || !values) return fixture;

  const fairMarkets = fixture?.fair_markets || {};
  const fairMarket = fairMarkets[marketKey] || {};
  return {
    ...fixture,
    fair_markets: {
      ...fairMarkets,
      [marketKey]: {
        ...fairMarket,
        lines: {
          ...(fairMarket.lines || {}),
          [normalizedLine]: values,
        },
      },
    },
  };
}
